import React, { useEffect, useState } from "react";
import { Building2, Users, Contact2, TrendingUp, Activity } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import CommandCenter from "../components/CommandCenter";
import { useAuth } from "../context/AuthContext";
import ClientDashboard from "./ClientDashboard";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from "recharts";

const STAGE_LABELS = {
  lead: "Lead", analysis: "Analisi", contact: "Contatto", documentation: "Documenti",
  proposal: "Proposta", negotiation: "Trattativa", agreement: "Accordo", closed: "Chiuso",
};
const STAGE_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981", "#22c55e", "#ef4444"];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.role === "client") return; // handled by ClientDashboard
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [user?.role]);

  if (user?.role === "client") return <ClientDashboard />;

  const stageData = stats ? Object.entries(stats.deals.by_stage).map(([k, v]) => ({ stage: STAGE_LABELS[k] || k, count: v })) : [];
  const propData = stats ? [
    { name: "Off market", value: stats.properties.off_market },
    { name: "Pre-market", value: stats.properties.pre_market },
    { name: "On market", value: stats.properties.on_market },
    { name: "Venduti", value: stats.properties.sold },
  ] : [];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Panoramica delle tue opportunità immobiliari off-market.</p>
        </div>
      </header>

      {/* Command Center */}
      <CommandCenter />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Building2} label="Immobili totali" value={stats?.properties.total} accent="text-blue-600" testid="kpi-properties" />
        <KPI icon={Contact2} label="Investitori attivi" value={stats?.kpi?.active_investors} accent="text-violet-600" testid="kpi-active-investors" />
        <KPI icon={TrendingUp} label="Valore pipeline" value={stats ? `€ ${Intl.NumberFormat("it-IT").format(Math.round(stats.deals.pipeline_value))}` : null} accent="text-emerald-600" testid="kpi-pipeline" />
        <KPI icon={TrendingUp} label="Margine stimato" value={stats ? `€ ${Intl.NumberFormat("it-IT").format(Math.round(stats.kpi?.total_margin || 0))}` : null} accent="text-amber-600" testid="kpi-margin" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SmallKPI label="ROI medio" value={stats ? `${stats.kpi?.avg_roi}%` : null} tone={stats?.kpi?.avg_roi >= 15 ? "positive" : "neutral"} />
        <SmallKPI label="Tempo medio chiusura" value={stats ? `${stats.kpi?.avg_close_days || 0} gg` : null} />
        <SmallKPI label="Valore complessivo" value={stats ? `€ ${Intl.NumberFormat("it-IT").format(Math.round(stats.kpi?.total_value || 0))}` : null} />
        <SmallKPI label="Pratiche in ritardo" value={stats?.kpi?.late_deals} tone={stats?.kpi?.late_deals > 0 ? "warning" : "positive"} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-border/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-medium">Trattative per fase</h3>
              <p className="text-xs text-muted-foreground">Distribuzione delle trattative nella pipeline</p>
            </div>
          </div>
          <div className="h-64">
            {stats ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="stage" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Skeleton className="w-full h-full" />}
          </div>
        </Card>

        <Card className="border-border/60 p-6">
          <h3 className="font-heading text-lg font-medium mb-4">Immobili per stato</h3>
          <div className="h-64">
            {stats ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={propData} dataKey="value" nameKey="name" outerRadius={70} innerRadius={40}>
                    {propData.map((_, i) => <Cell key={i} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Skeleton className="w-full h-full" />}
          </div>
        </Card>
      </div>

      {/* Recent activities */}
      <Card className="border-border/60 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-primary" />
          <h3 className="font-heading text-lg font-medium">Attività recenti</h3>
        </div>
        {!stats ? <Skeleton className="h-40 w-full" /> : (
          <div className="divide-y divide-border/60">
            {stats.recent_activities.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Nessuna attività ancora. Inizia creando il tuo primo immobile.</p>}
            {stats.recent_activities.map((a) => (
              <div key={a.id} className="py-3 flex items-center gap-3 text-sm" data-testid="activity-item">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="font-medium">{a.user_name || "Sistema"}</span>
                <span className="text-muted-foreground">{a.action}</span>
                <span className="text-xs bg-muted rounded px-2 py-0.5">{a.entity_type}</span>
                <span className="text-muted-foreground truncate">{a.detail}</span>
                <span className="ml-auto text-xs text-muted-foreground tabular-nums">{new Date(a.created_at).toLocaleString("it-IT")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="border-border/60 p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
          <p className="mt-3 font-heading text-3xl font-semibold tracking-tight tabular-nums">
            {value === null || value === undefined ? "—" : value}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-md bg-muted flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}

function SmallKPI({ label, value, tone }) {
  const toneCls = tone === "positive" ? "text-emerald-600" : tone === "warning" ? "text-rose-600" : "text-foreground";
  return (
    <Card className="border-border/60 p-4">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
      <p className={`mt-1 font-heading text-xl font-semibold tabular-nums ${toneCls}`}>
        {value === null || value === undefined ? "—" : value}
      </p>
    </Card>
  );
}
